import { useFieldArray, useWatch } from "react-hook-form"
import CountrySelect from "@/components/forms/country.select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import StateSelect from "@/components/forms/state.select"
import { Input } from "@/components/ui/input"
import { useStates } from "@/api/data"
import { z } from "zod"
import { customerFormSchema } from "./customer.schema"

interface CustomerAddressFormProps {
  index: number;
}

export default function CustomerAddressForm({
  index,
}: CustomerAddressFormProps) {
  const countryId = useWatch({ name: `addresses.${index}.countryId` })
  const { states } = useStates(countryId)
  const { fields, update } = useFieldArray<z.input<typeof customerFormSchema>>({
    name: "addresses",
  })

  const onCountryChange = (value: string) => {
    const values = { ...fields[index], stateId: undefined, countryId: value }
    update(index, values)
  }

  return (
    <>
      <div className="flex flex-row gap-4">
        <FormField
          name={`addresses.${index}.countryId`}
          render={({ field }) => (
            <FormItem className="w-6/12">
              <FormLabel required>Country</FormLabel>
              <FormControl>
                <CountrySelect
                  onChange={(v) => onCountryChange(v)}
                  defaultValue={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {countryId !== "" && (
          <FormField
            name={`addresses.${index}.stateId`}
            render={({ field }) => (
              <FormItem className={states.length ? "w-6/12" : ""}>
                <FormLabel>State</FormLabel>
                <FormControl>
                  <StateSelect
                    countryId={countryId}
                    onChange={field.onChange}
                    value={field.value}
                    defaultValue={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      <FormField
        name={`addresses.${index}.address`}
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Address line 1</FormLabel>
            <FormControl>
              <Input placeholder="Street and number" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name={`addresses.${index}.addressExtra`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address line 2</FormLabel>
            <FormControl>
              <Input placeholder="Floor, apartment or entrance" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex flex-row gap-4">
        <FormField
          name={`addresses.${index}.city`}
          render={({ field }) => (
            <FormItem className="w-6/12">
              <FormLabel required>City</FormLabel>
              <FormControl>
                <Input placeholder="City" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name={`addresses.${index}.postalCode`}
          render={({ field }) => (
            <FormItem className="w-6/12">
              <FormLabel required>Zip/Postal Code</FormLabel>
              <FormControl>
                <Input placeholder="Zip/Postal Code" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  )
}
